import { isObjectLiteral } from '../data';
import { pickString } from '../string';
import { hasEnvelope } from './normalizeError';
/**
 * Parses the structured run-error tree out of a terminal pipeline-run `error` payload into the normalized
 * {@link RunErrors} model consumed by the gem tooltip, the failure banner, and the details drawer.
 *
 * Returns `null` when the payload carries no structured gem causes, so callers fall through to today's
 * unchanged rendering. The check is purely on payload shape (an `ErrorEnvelope` detail with `causes[]`),
 * never on fabric type — so any fabric that adopts this error shape is handled automatically.
 *
 * Purely additive: does not touch `normalizeError`.
 */
const FALLBACK_MESSAGE = 'Pipeline execution failed';
const ERROR_ENVELOPE_TYPE_SUFFIX = 'ErrorEnvelope';
// The details[] entry that carries the recursive cause tree: prefer @type …ErrorEnvelope, else any node with causes[].
function findStructuredRoot(envelope) {
    const details = envelope.details;
    if (!Array.isArray(details))
        return undefined;
    const byType = details.find((d) => isObjectLiteral(d) && typeof d['@type'] === 'string' && d['@type'].endsWith(ERROR_ENVELOPE_TYPE_SUFFIX));
    if (byType && Array.isArray(byType.causes))
        return byType;
    return details.find((d) => isObjectLiteral(d) && Array.isArray(d.causes));
}
function paramValue(node, key) {
    var _a;
    const param = (_a = node.parameters) === null || _a === void 0 ? void 0 : _a[key];
    return isObjectLiteral(param) ? pickString(param.string_value) : undefined;
}
function gemRef(node) {
    var _a;
    const refs = node.entity_refs;
    if (!Array.isArray(refs))
        return undefined;
    return (_a = refs.find((r) => isObjectLiteral(r) && isObjectLiteral(r.gem))) === null || _a === void 0 ? void 0 : _a.gem;
}
function mapRemediationHints(hints) {
    if (!Array.isArray(hints))
        return [];
    return hints
        .filter((h) => isObjectLiteral(h))
        .map((h) => {
        var _a;
        return ({
            actionType: typeof h.action_type === 'number' ? h.action_type : undefined,
            description: (_a = pickString(h.description_template)) !== null && _a !== void 0 ? _a : ''
        });
    })
        .filter((h) => h.description.length > 0);
}
function rawCauseOf(node) {
    const child = Array.isArray(node.causes) ? node.causes.find(isObjectLiteral) : undefined;
    return child ? pickString(child.developer_message, child.user_message) : undefined;
}
// A per-gem failure, detected by STRUCTURE not `code` (the code enum is subsystem-specific): a gem node carries a
// gem ref or gem identity params; a lone underlying cause doesn't.
function isGemNode(cause) {
    return !!gemRef(cause) || !!paramValue(cause, 'processId') || !!paramValue(cause, 'gemId');
}
// Normalizes a single structured error node into a GemError. Exported so the live `gemProgress.exception.error_envelope`
// (a bare gem node, no `causes[]` wrapper that extractRunErrors needs) reuses the exact same mapping.
export function extractGemError(node) {
    var _a, _b, _c, _d, _e, _f;
    // `parameters` is BE's authoritative source for gem identity; `entity_refs.gem` is only a fallback.
    const gem = gemRef(node);
    const failureMessage = paramValue(node, 'failureMessage');
    return {
        processId: paramValue(node, 'processId'),
        gemId: (_a = paramValue(node, 'gemId')) !== null && _a !== void 0 ? _a : gem === null || gem === void 0 ? void 0 : gem.gem_id,
        pipelineId: (_b = paramValue(node, 'pipelineId')) !== null && _b !== void 0 ? _b : gem === null || gem === void 0 ? void 0 : gem.pipeline_id,
        displayName: (_c = paramValue(node, 'displayName')) !== null && _c !== void 0 ? _c : gem === null || gem === void 0 ? void 0 : gem.display_name,
        code: node.code,
        userMessage: (_d = pickString(node.user_message, failureMessage, node.developer_message)) !== null && _d !== void 0 ? _d : FALLBACK_MESSAGE,
        developerMessage: pickString(node.developer_message),
        detailMessage: (_e = pickString(failureMessage, node.developer_message)) !== null && _e !== void 0 ? _e : '',
        rawCause: rawCauseOf(node),
        severity: (_f = node.classification) === null || _f === void 0 ? void 0 : _f.severity,
        remediationHints: mapRemediationHints(node.remediation_hints),
        raw: node
    };
}
export function extractRunErrors(raw) {
    var _a;
    if (!hasEnvelope(raw))
        return null;
    const { envelope } = raw;
    const root = findStructuredRoot(envelope);
    if (!root || !Array.isArray(root.causes))
        return null;
    const gemList = root.causes.filter(isObjectLiteral).filter(isGemNode).map(extractGemError);
    if (gemList.length === 0)
        return null;
    const gems = {};
    for (const gem of gemList) {
        if (gem.processId)
            gems[gem.processId] = gem;
    }
    return {
        pipeline: {
            code: root.code,
            userMessage: (_a = pickString(root.user_message, envelope.user_message, envelope.message)) !== null && _a !== void 0 ? _a : FALLBACK_MESSAGE,
            detailMessage: pickString(root.developer_message, envelope.message),
            errorId: pickString(root.error_id, envelope.error_id)
        },
        gems,
        gemList,
        raw
    };
}
